import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Typography } from "antd";
import { ApartmentOutlined, RobotOutlined, FileTextOutlined, CheckCircleFilled } from "@ant-design/icons";
import { SbTable, SbTag, SbButton, SbDrawer, SbEmpty, SbSpin, SbBetaTag } from "uiCore";
import { PageHeaderStyled, HeaderLeftStyled, HeaderRightStyled, HeadingRowStyled } from "styles/common.styles";
import { formatTimestamp, toTimestampMs } from "Utils/timeFormat";
import {
  PageTransitionStyled,
  ClickableTableWrapperStyled,
  EntryCardsRowStyled,
  EntryCardStyled,
  EntryIconSlotStyled,
  EntryIconBadgeStyled,
  SectionTitleRowStyled,
  ImportEmptyStyled,
  ProjectNameCellStyled,
} from "./orgDesign.styles";
import OrgContextTab from "./components/OrgContextTab";
import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
import {
  usePublishedOrgRedesignVersions,
  useOrgDesignWorkspace,
  useCanAccessCurrentOrgFlow,
  useCanAccessPaicOrgDesignFlow,
} from "./useOrgDesignData";
import { TEXT, BASELINE_TAG, ENTRY_OPTION, activatableRowProps, getFilters } from "./orgDesign.constants";

const { Title, Text } = Typography;

const ANALYTICS_FROM = "org_design_landing";
const EVENTS = ANALYTICS_EVENTS.ORG_DESIGN;

const entryOptionForSourceType = (sourceType) =>
  sourceType === "PAIC" ? ENTRY_OPTION.PAIC_RECOMMENDED : ENTRY_OPTION.EXISTING_ORG;

// Fields match GET /versions verbatim (Org Redesign API doc §8) — a
// lightweight, org-wide list of PUBLISHED versions only. Built from
// `versions` (not a module-level constant) so filters reflect whatever
// values actually appear in the current list.
//
// Column order follows BUG #1800, with the single "Version" column BUG #1940
// then split in two: Project Name, Org Version, PAIC Version, Source,
// Status, Created By, Updated, Published. sb_org_redesign_code has no
// dedicated column of its own here (that order leaves no room for one) — it
// renders as a muted second line under the project name instead, so it's
// still visible without disturbing the requested layout.
const formatCreatedBy = (record) => {
  const fullName = [record.triggered_by_user_first_name, record.triggered_by_user_last_name]
    .filter(Boolean)
    .join(" ");
  return fullName || <Text type="secondary">—</Text>;
};

// project_name is PAIC-only and always null for CURRENT ORG versions — those
// carry their user-typed name in org_redesign_name instead, so the column
// falls back to it rather than showing blank for every existing-org version.
const displayProjectName = (record) => record.project_name || record.org_redesign_name;

const buildVersionColumns = (versions) => [
  {
    title: "Project Name",
    dataIndex: "project_name",
    filters: getFilters(versions, "project_name", displayProjectName),
    onFilter: (value, record) => displayProjectName(record) === value,
    render: (_, record) => (
      <ProjectNameCellStyled>
        <span>{displayProjectName(record) || <Text type="secondary">—</Text>}</span>
        {record.sb_org_redesign_code && <span className="code-text">{record.sb_org_redesign_code}</span>}
      </ProjectNameCellStyled>
    ),
  },
  // `version` is the Org Design's own iteration number — 1 for a PAIC-
  // sourced row today, but can climb past 1 for a from-scratch (CURRENT ORG)
  // design that's been saved over multiple times (BUG #1940).
  {
    title: "Org Version",
    dataIndex: "version",
    render: (v) => (v != null ? <Text strong>{`Version ${v}`}</Text> : <Text type="secondary">—</Text>),
  },
  // `selection_version` is which PAIC agent-selection run this design was
  // generated from — only PAIC-sourced rows carry one; a from-scratch
  // (CURRENT ORG) row never had a PAIC selection to begin with, so this is
  // always blank for it (BUG #1940).
  {
    title: "PAIC Version",
    dataIndex: "selection_version",
    render: (v) => (v != null ? <Text strong>{`Version ${v}`}</Text> : <Text type="secondary">—</Text>),
  },
  {
    title: "Source",
    dataIndex: "source_type",
    filters: getFilters(versions, "source_type"),
    onFilter: (value, record) => record.source_type === value,
    render: (v) => {
      const entryOption = entryOptionForSourceType(v);
      return <SbTag color={entryOption === ENTRY_OPTION.PAIC_RECOMMENDED ? "processing" : "default"}>{BASELINE_TAG[entryOption]}</SbTag>;
    },
  },
  {
    title: "Status",
    dataIndex: "status",
    filters: getFilters(versions, "status"),
    onFilter: (value, record) => record.status === value,
    render: (v) => <SbTag color="success">{v}</SbTag>,
  },
  {
    title: "Created By",
    key: "created_by",
    render: (_, record) => formatCreatedBy(record),
  },
  {
    title: "Updated",
    dataIndex: "last_updated_time_stamp",
    sorter: (a, b) => toTimestampMs(a.last_updated_time_stamp) - toTimestampMs(b.last_updated_time_stamp),
    // Most recently updated first on load; the header still toggles it.
    defaultSortOrder: "descend",
    render: (v) => formatTimestamp(v) || <Text type="secondary">—</Text>,
  },
  {
    title: "Published",
    dataIndex: "published_status",
    render: () => (
      <SbTag color="success" icon={<CheckCircleFilled />}>Published</SbTag>
    ),
  },
];

const OrgDesignLanding = () => {
  const navigate = useNavigate();
  const { versions, loading, opening, openVersion } = usePublishedOrgRedesignVersions();
  const { startingExistingOrg, startFromExistingOrg } = useOrgDesignWorkspace();
  const [contextDrawerOpen, setContextDrawerOpen] = useState(false);
  const canAccessCurrentOrg = useCanAccessCurrentOrgFlow();
  const canAccessPaic = useCanAccessPaicOrgDesignFlow();
  const track = useAnalytics(ANALYTICS_FROM);

  // Which entry points a user can even see is the thing worth knowing here —
  // an empty Org Design funnel reads very differently when neither card is
  // rendered than when both are and nobody clicks.
  useEffect(() => {
    track(EVENTS.LANDING_VIEWED, { canAccessCurrentOrg, canAccessPaic });
  }, [track, canAccessCurrentOrg, canAccessPaic]);

  const handleStartExisting = async () => {
    if (startingExistingOrg) return;
    track(EVENTS.START_FROM_CURRENT_ORG);
    const loaded = await startFromExistingOrg();
    if (loaded) navigate("/org-design/workspace");
  };

  const handleOpenPaicImport = () => {
    track(EVENTS.OPEN_PAIC_IMPORT);
    navigate("/org-design/paic-import");
  };

  const handleOpenVersion = async (record) => {
    if (opening) return;
    track(EVENTS.VERSION_OPENED, {
      orgRedesignId: record.sb_org_redesign_unique_id,
      sourceType: record.source_type,
      version: record.version,
    });
    const opened = await openVersion(record);
    if (opened) navigate("/org-design/workspace");
  };

  return (
    <PageTransitionStyled>
      <PageHeaderStyled>
        <HeaderLeftStyled>
          <HeadingRowStyled>
            <Title level={4}>{TEXT.PAGE_TITLE}</Title>
          </HeadingRowStyled>
          <Text type="secondary">{TEXT.PAGE_DESCRIPTION}</Text>
        </HeaderLeftStyled>
        <HeaderRightStyled>
          <SbButton
            buttonType="secondary"
            icon={<FileTextOutlined />}
            onClick={() => {
              track(EVENTS.ORG_CONTEXT_OPENED);
              setContextDrawerOpen(true);
            }}
          >
            {TEXT.ORG_CONTEXT_TAB}
            <SbBetaTag label="Coming soon" />
          </SbButton>
        </HeaderRightStyled>
      </PageHeaderStyled>

      <SectionTitleRowStyled $topGap>
        <Title level={5}>Start a New Organisation Design</Title>
      </SectionTitleRowStyled>
      {canAccessCurrentOrg || canAccessPaic ? (
        <EntryCardsRowStyled>
          {canAccessCurrentOrg && (
            <EntryCardStyled
              onClick={handleStartExisting}
              $loading={startingExistingOrg}
              {...activatableRowProps(handleStartExisting)}
            >
              <EntryIconSlotStyled>
                {startingExistingOrg ? (
                  <SbSpin size="default" />
                ) : (
                  <EntryIconBadgeStyled>
                    <ApartmentOutlined />
                  </EntryIconBadgeStyled>
                )}
              </EntryIconSlotStyled>
              <div className="entry-title">{TEXT.OPTION_A_TITLE}</div>
              <div className="entry-desc">{startingExistingOrg ? "Loading your organization structure…" : TEXT.OPTION_A_DESC}</div>
            </EntryCardStyled>
          )}
          {canAccessPaic && (
            <EntryCardStyled
              onClick={handleOpenPaicImport}
              {...activatableRowProps(handleOpenPaicImport)}
            >
              <EntryIconSlotStyled>
                <EntryIconBadgeStyled>
                  <RobotOutlined />
                </EntryIconBadgeStyled>
              </EntryIconSlotStyled>
              <div className="entry-title">{TEXT.OPTION_B_TITLE}</div>
              <div className="entry-desc">{TEXT.OPTION_B_DESC}</div>
            </EntryCardStyled>
          )}
        </EntryCardsRowStyled>
      ) : (
        <ImportEmptyStyled>
          <SbEmpty description="You don't have access to start a new organization design." />
        </ImportEmptyStyled>
      )}

      <SectionTitleRowStyled $sectionGap>
        <Title level={5}>{TEXT.PREVIOUS_ITERATIONS}</Title>
      </SectionTitleRowStyled>
      {!loading && versions.length === 0 ? (
        <ImportEmptyStyled>
          <SbEmpty description="No published Organisation Designs yet — start a new one above." />
        </ImportEmptyStyled>
      ) : (
        <ClickableTableWrapperStyled>
          <SbTable
            columns={buildVersionColumns(versions)}
            dataSource={versions}
            rowKey="sb_org_redesign_unique_id"
            loading={loading || opening}
            sticky
            pagination={{ defaultPageSize: 10, pageSizeOptions: ["10", "20", "30"], showSizeChanger: true }}
            onRow={(record) => ({ onClick: () => handleOpenVersion(record), ...activatableRowProps(() => handleOpenVersion(record)) })}
          />
        </ClickableTableWrapperStyled>
      )}

      <SbDrawer
        open={contextDrawerOpen}
        onClose={() => setContextDrawerOpen(false)}
        title={TEXT.ORG_CONTEXT_TAB}
        width="480px"
      >
        <OrgContextTab />
      </SbDrawer>
    </PageTransitionStyled>
  );
};

export default OrgDesignLanding;
